import { Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { ModalDirective } from 'ngx-bootstrap';
import { Observable, Subscription } from 'rxjs';
import { UploadJobService, UploadState } from '../../../top-shared/upload-progress/upload-job.service';

interface FieldMapping { field: string; column: number; auto: boolean; required?: boolean; ignored?: boolean; }

/** Rules of a template: required fields, a pair of which exactly one must be matched, fields left out of the upload, notes. */
export interface SheetMappingOptions {
  required?: string[];
  exactlyOne?: string[];
  ignored?: string[];
  notes?: string[];
}

/**
 * Upload with column matching: the picked sheet's columns are matched to the template's fields (same or similar
 * name → preselected, otherwise left empty for the user), then the sheet is rebuilt in the template's column order
 * and uploaded, with the progress shown at the bottom of the popup. When the server answers { jobId } (a background
 * upload), the popup follows the job with the uploads' progress bar until the result is in.
 */
@Component({
  selector: 'crm-sheet-mapping',
  templateUrl: './sheet-mapping.component.html',
  styleUrls: ['./sheet-mapping.component.css']
})
export class SheetMappingComponent {
  @ViewChild('modal') modal: ModalDirective;
  /** The server's answer to the upload (same body the plain upload returned). */
  @Output() uploaded = new EventEmitter<any>();

  fileName = '';
  columns: string[] = [];
  mappings: FieldMapping[] = [];
  rows: string[][] = [];
  private send: (file: File) => Observable<any>;
  private request: Subscription;
  uploading = false;
  progress = 0;
  job: UploadState;
  error = '';
  notes: string[] = [];
  private exactlyOne: string[] = [];

  constructor(private jobs: UploadJobService) { }

  /** Reads the sheet and opens the popup; `send` uploads the rebuilt file (an HttpEvent stream reports progress). */
  open(file: File, templateFields: string[], send: (file: File) => Observable<any>, options: SheetMappingOptions = {}) {
    this.fileName = file.name;
    this.send = send;
    this.error = '';
    this.uploading = false;
    this.progress = 0;
    const reader = new FileReader();
    reader.onload = () => {
      const all = parseCsv(String(reader.result || ''));
      this.columns = (all[0] || []).map(c => c.trim());
      this.rows = all.slice(1).filter(r => r.some(v => v.trim() !== ''));
      this.mappings = matchColumns(templateFields.map(f => f.trim()).filter(f => f), this.columns);
      const has = (list: string[], field: string) => (list || []).some(x => normalize(x) === normalize(field));
      this.exactlyOne = (options.exactlyOne || []).filter(f => !has(options.ignored, f));
      this.notes = options.notes || [];
      this.mappings.forEach(m => {
        m.ignored = has(options.ignored, m.field);
        m.required = !m.ignored && (has(options.required, m.field) || (this.exactlyOne.length === 1 && has(this.exactlyOne, m.field)));
        if (m.ignored) { m.column = -1; m.auto = false; }
      });
      this.modal.show();
    };
    reader.readAsText(file);
  }

  get unmatched(): number {
    return this.mappings.filter(m => m.column < 0 && !m.ignored).length;
  }

  isOneOf(m: FieldMapping): boolean {
    return this.exactlyOne.length > 1 && this.exactlyOne.some(f => normalize(f) === normalize(m.field));
  }

  /** What still blocks the upload: required fields not matched, and the "exactly one of" rule. */
  get problems(): string[] {
    const list: string[] = [];
    const missing = this.mappings.filter(m => m.required && m.column < 0).map(m => m.field);
    if (missing.length) list.push('Match the required field' + (missing.length > 1 ? 's' : '') + ': ' + missing.join(', ') + '.');
    if (this.exactlyOne.length > 1) {
      const pair = this.mappings.filter(m => this.isOneOf(m));
      const matched = pair.filter(m => m.column >= 0).length;
      const names = pair.map(m => m.field).join(' or ');
      if (matched === 0) list.push('Match ' + names + '.');
      if (matched > 1) list.push('Upload by ' + names + ', not both: set one of them to "Not in the sheet".');
    }
    return list;
  }

  sample(m: FieldMapping): string {
    if (m.column < 0) return '';
    return this.rows.slice(0, 3).map(r => r[m.column] || '').filter(v => v !== '').join(' · ');
  }

  usedElsewhere(m: FieldMapping, column: number): boolean {
    return this.mappings.some(x => x !== m && x.column === column);
  }

  pick(m: FieldMapping, value: any) {
    m.column = +value;
    m.auto = false;
  }

  upload() {
    if (this.problems.length) return;
    const csv = [this.mappings.map(m => csvCell(m.field)).join(',')]
      .concat(this.rows.map(r => this.mappings.map(m => csvCell(m.column >= 0 ? (r[m.column] || '') : '')).join(',')))
      .join('\r\n');
    const file = new File(['﻿' + csv], this.fileName, { type: 'text/csv' });
    this.uploading = true;
    this.progress = 0;
    this.job = null;
    this.error = '';
    this.request = this.jobs.run(this.send(file)).subscribe(state => {
      this.job = state;
      if (!state.done) return;
      if (state.error) { this.uploading = false; this.error = state.error; return; }
      this.finish(state.result);
    });
  }

  cancel() {
    if (this.request) this.request.unsubscribe();
    this.uploading = false;
    this.modal.hide();
  }

  private finish(body: any) {
    this.progress = 100;
    this.uploading = false;
    this.modal.hide();
    this.uploaded.emit(body);
  }
}

/** CSV text → rows (quoted cells, "" escapes, CRLF / LF; "," or ";" — whichever the header uses more). */
export function parseCsv(text: string): string[][] {
  text = text.replace(/^﻿/, '');
  const firstLine = text.split(/\r?\n/, 1)[0] || '';
  const sep = (firstLine.match(/;/g) || []).length > (firstLine.match(/,/g) || []).length ? ';' : ',';
  const rows: string[][] = [];
  let row: string[] = [], cell = '', quoted = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (quoted) {
      if (ch === '"' && text[i + 1] === '"') { cell += '"'; i++; }
      else if (ch === '"') quoted = false;
      else cell += ch;
    } else if (ch === '"') quoted = true;
    else if (ch === sep) { row.push(cell); cell = ''; }
    else if (ch === '\n' || ch === '\r') {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(cell); rows.push(row); row = []; cell = '';
    } else cell += ch;
  }
  if (cell !== '' || row.length) { row.push(cell); rows.push(row); }
  return rows;
}

function csvCell(v: string): string {
  return /[",\r\n]/.test(v) ? '"' + v.replace(/"/g, '""') + '"' : v;
}

const normalize = (s: string) => (s || '').toLowerCase().replace(/[\s_\-.\/()]+/g, '');

function similarity(a: string, b: string): number {
  if (!a || !b) return 0;
  if (a === b) return 1;
  if (a.indexOf(b) >= 0 || b.indexOf(a) >= 0) return 0.9;
  const d: number[][] = [];
  for (let i = 0; i <= a.length; i++) { d[i] = [i]; }
  for (let j = 1; j <= b.length; j++) d[0][j] = j;
  for (let i = 1; i <= a.length; i++)
    for (let j = 1; j <= b.length; j++)
      d[i][j] = Math.min(d[i - 1][j] + 1, d[i][j - 1] + 1, d[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
  return 1 - d[a.length][b.length] / Math.max(a.length, b.length);
}

/** Each field gets the most similar unused column (best pairs first); below 0.8 similarity it stays empty. */
function matchColumns(fields: string[], columns: string[]): FieldMapping[] {
  const pairs: { f: number; c: number; s: number }[] = [];
  fields.forEach((f, fi) => columns.forEach((c, ci) => {
    const s = similarity(normalize(f), normalize(c));
    if (s >= 0.8) pairs.push({ f: fi, c: ci, s });
  }));
  pairs.sort((x, y) => y.s - x.s);
  const result = fields.map(field => ({ field, column: -1, auto: false }));
  const usedCols = new Set<number>();
  pairs.forEach(p => {
    if (result[p.f].column < 0 && !usedCols.has(p.c)) { result[p.f].column = p.c; result[p.f].auto = true; usedCols.add(p.c); }
  });
  return result;
}
